<template>
  <div class="min-height-200">
    <!-- Existing attachments from answers -->
    <template v-if="attachments.length > 0">
      <div v-if="assignment" class="default-base-color rounded p-4 mb-primary">
        <div class="d-flex justify-content-between align-items-center">
          <a class="mb-0 text-primary"
             download
             :href="urlGenerator(assignment.attachment)"
             target="_blank">
            {{ $t('assignment') }} - {{ applicant.full_name }}
          </a>
          <div class="d-flex justify-content-start">
            <a download
               :href="urlGenerator(assignment.attachment)"
               target="_blank"
               class="text-muted default-base-color width-30 height-30 rounded d-inline-flex align-items-center justify-content-center">
              <app-icon name="download" class="size-14"/>
            </a>
          </div>
        </div>
      </div>

      <div v-if="resume" class="default-base-color rounded p-4 mb-primary">
        <div class="d-flex justify-content-between align-items-center">
          <a class="mb-0 text-primary"
             :href="urlGenerator(resume.attachment)"
             target="_blank">
            {{ $t('resume') }} - {{ applicant.full_name }}
          </a>
          <div class="d-flex justify-content-start">
            <a download
               :href="`/storage/attachments/${resume.attachment}`"
               target="_blank"
               class="text-muted default-base-color width-30 height-30 rounded d-inline-flex align-items-center justify-content-center">
              <app-icon name="download" class="size-14"/>
            </a>
          </div>
        </div>
      </div>

      <template v-if="customAttachment && customAttachment.length">
        <div v-for="(custom, index) in customAttachment"
             :key="`custom-attachment-${index}`"
             class="default-base-color rounded p-4 mb-primary">
          <div class="d-flex justify-content-between align-items-center">
            <a class="mb-0 text-primary"
               :href="urlGenerator(custom.attachment)"
               target="_blank">
              {{ $t(custom.question) }} - {{ applicant.full_name }}
            </a>
            <div class="d-flex justify-content-start">
              <a
                  :href="urlGenerator(custom.attachment)"
                  target="_blank"
                  download
                  class="text-muted default-base-color width-30 height-30 rounded d-inline-flex align-items-center justify-content-center">
                <app-icon name="download" class="size-14"/>
              </a>
            </div>
          </div>
        </div>
      </template>
    </template>

    <!-- Additional uploaded documents -->
    <div class="mt-4">
      <h5 class="mb-3">{{ $t('additional_documents') || 'Additional Documents' }}</h5>

      <div v-if="docError" class="alert alert-danger mb-3">
        {{ docError }}
      </div>

      <div v-if="docSuccess" class="alert alert-success mb-3">
        {{ docSuccess }}
      </div>

      <!-- Upload box only for candidate -->
      <div v-if="isCandidate" class="default-base-color rounded p-4 mb-primary">
        <div class="form-group mb-3">
          <label class="mb-1">{{ $t('document_name') || 'Document name' }}</label>
          <input
              type="text"
              class="form-control"
              v-model="newDocName"
              :placeholder="$t('enter_document_name') || 'Enter document name'"
          />
        </div>

        <div class="form-group mb-3">
          <label class="mb-1">{{ $t('document_file') || 'Document file (PDF or Image)' }}</label>
          <input
              type="file"
              class="form-control"
              accept="application/pdf,image/*"
              @change="onFileChange"
          />
          <small class="text-muted d-block mt-1">
            PDF, PNG, JPG, JPEG, WEBP, GIF (max 5MB)
          </small>
        </div>

        <button
            class="btn btn-primary"
            :disabled="docUploading"
            @click.prevent="uploadDocument"
        >
          <span v-if="docUploading">{{ $t('uploading') || 'Uploading...' }}</span>
          <span v-else>{{ $t('upload') || 'Upload' }}</span>
        </button>
      </div>

      <div v-if="docLoading" class="text-muted">
        {{ $t('loading') || 'Loading...' }}
      </div>

      <template v-else>
        <div v-if="documents.length">
          <div
              v-for="doc in documents"
              :key="doc.id"
              class="default-base-color rounded p-4 mb-primary"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex flex-column">
                <a class="mb-1 text-primary" :href="doc.url" target="_blank">
                  {{ doc.name }}
                </a>
                <small class="text-muted">
                  {{ doc.mime_type }} • {{ doc.created_at }}
                </small>
              </div>

              <div class="d-flex align-items-center">
                <a
                    :href="doc.url"
                    target="_blank"
                    download
                    class="text-muted default-base-color width-30 height-30 rounded d-inline-flex align-items-center justify-content-center mr-2"
                    title="Download"
                >
                  <app-icon name="download" class="size-14"/>
                </a>

                <button
                    v-if="isCandidate"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="docDeletingId === doc.id"
                    @click.prevent="deleteDocument(doc.id)"
                >
                  <span v-if="docDeletingId === doc.id">{{ $t('deleting') || 'Deleting...' }}</span>
                  <span v-else>{{ $t('delete') || 'Delete' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <app-empty-data-block
            v-if="!assignment && !resume && !customAttachment.length && !documents.length"
            :message="$t('this_candidate_have_no_attachment')"
        />
      </template>
    </div>
  </div>
</template>

<script>
import {urlGenerator} from "../../../../Helpers/AxiosHelper";
import {TENANT_BASE_URL} from "../../../../Config/UrlHelper";

export default {
  name: "CandidateAttachments",
  props: {
    jobApplicantId: { required: true },
    isCandidate: {
      type: Boolean,
      default: false
    },
    applyForm: {},
    applicant: {},
    answers: {
      type: Array,
      default: () => ([])
    }
  },
  data() {
    return {
      urlGenerator,

      // additional docs
      documents: [],
      docLoading: false,
      docUploading: false,
      docDeletingId: null,
      docError: null,
      docSuccess: null,

      newDocName: "",
      newDocFile: null,
    }
  },
  computed: {
    attachments() {
      return (this.answers || []).filter(item => item.attachment);
    },
    resume() {
      return this.attachments.find(item => (item.question || '').includes('resume'));
    },
    assignment() {
      return this.attachments.find(item => item.question.includes('attachment'));
    },
    customAttachment() {
      return this.attachments.filter(item => !item.question.includes('resume') && !item.question.includes('attachment'));
    }
  },
  methods: {
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      this.newDocFile = file || null;
    },

    apiUrl(path) {
      return `${TENANT_BASE_URL}${path}`;
    },

    async fetchDocuments() {
      if (!this.jobApplicantId) return;

      this.docLoading = true;
      this.docError = null;
      this.docSuccess = null;

      try {
        const res = await axios.get(this.apiUrl(`/app/job-applicant/${this.jobApplicantId}/documents`));
        this.documents = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.docError = e?.response?.data?.message || e?.message || 'Failed to load documents';
      } finally {
        this.docLoading = false;
      }
    },

    async uploadDocument() {
      this.docError = null;
      this.docSuccess = null;

      if (!this.newDocName || !this.newDocName.trim()) {
        this.docError = "Document name is required";
        return;
      }
      if (!this.newDocFile) {
        this.docError = "Please choose a file (PDF or image)";
        return;
      }

      const fd = new FormData();
      fd.append('name', this.newDocName.trim());
      fd.append('file', this.newDocFile);

      this.docUploading = true;

      try {
        const res = await axios.post(
            this.apiUrl(`/app/job-applicant/${this.jobApplicantId}/documents`),
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        this.docSuccess = res?.data?.message || 'Document uploaded successfully';

        this.newDocName = "";
        this.newDocFile = null;

        // reset file input visually
        this.$nextTick(() => {
          const inputs = this.$el.querySelectorAll('input[type="file"]');
          inputs.forEach(i => (i.value = ""));
        });

        await this.fetchDocuments();
      } catch (e) {
        this.docError = e?.response?.data?.message || e?.message || 'Upload failed';
      } finally {
        this.docUploading = false;
      }
    },

    async deleteDocument(documentId) {
      if (!documentId) return;

      this.docDeletingId = documentId;
      this.docError = null;
      this.docSuccess = null;

      try {
        const res = await axios.delete(
            this.apiUrl(`/app/job-applicant/${this.jobApplicantId}/documents/${documentId}`)
        );

        this.docSuccess = res?.data?.message || 'Document deleted successfully';
        await this.fetchDocuments();
      } catch (e) {
        this.docError = e?.response?.data?.message || e?.message || 'Delete failed';
      } finally {
        this.docDeletingId = null;
      }
    }
  },
  mounted() {
    this.fetchDocuments();
  }
}
</script>
